import type { ActionContext } from 'vuex'
import timesheetService from '@/services/timesheet.service'

// Project response from /v1/master/sites API
type V2Site = {
  projectKey: number
  companyID: string
  projectID: string
  name: string
  description: string
  projectClassification: string
  parentProjectID: string
  isSite: boolean
  isJob: boolean
  isAsset: boolean
  isWorkOrder: boolean
  isActive: boolean
}
type V2Equipment = { EquipmentId: string; Name: string; Description?: string; SiteId?: string; ProjectId?: string }
type V2WorkOrder = { WorkOrderId: string; Name: string; Description?: string; EquipmentId?: string; ProjectId?: string }
type V2Project = { ProjectId: string; CompanyId: string; Name: string; SiteId?: string; Description?: string }
type V2Task = { TaskId: string; Name: string; Description?: string; SiteId?: string; ProjectId?: string }
type V2DropdownItem = { id: string; name: string; description?: string; companyId?: string; siteId?: string; equipmentId?: string; projectId?: string }

type V2State = {
  loading: boolean
  error: string | null
  sites: V2DropdownItem[]
  equipments: V2DropdownItem[]
  workOrders: V2DropdownItem[]
  projects: V2DropdownItem[]
  tasks: V2DropdownItem[]  // For Overhead Task Codes only
  activities: V2DropdownItem[]  // For Force Activities (separate from overhead tasks)
}


const state: V2State = {
  loading: false,
  error: null,
  sites: [],
  equipments: [],
  workOrders: [],
  projects: [],
  tasks: [],  // For Overhead Task Codes only
  activities: [],  // For Force Activities
}

// Pending promises for request deduplication
const pendingRequests: Record<string, Promise<any>> = {}

const mutations = {
  SET_LOADING(s: V2State, value: boolean) {
    s.loading = value
  },
  SET_ERROR(s: V2State, message: string | null) {
    s.error = message
  },
  SET_SITES(s: V2State, items: V2DropdownItem[]) {
    s.sites = items
  },
  SET_EQUIPMENTS(s: V2State, items: V2DropdownItem[]) {
    s.equipments = items
  },
  SET_WORK_ORDERS(s: V2State, items: V2DropdownItem[]) {
    s.workOrders = items
  },
  SET_PROJECTS(s: V2State, items: V2DropdownItem[]) {
    s.projects = items
  },
  SET_TASKS(s: V2State, items: V2DropdownItem[]) {
    s.tasks = items
  },
  SET_ACTIVITIES(s: V2State, items: V2DropdownItem[]) {
    s.activities = items
  },
  SET_ASSETS(s: V2State, items: V2DropdownItem[]) {
    s.equipments = items
  },
}

const actions = {
  async loadSites(ctx: ActionContext<V2State, any>, companyId?: string) {
    const cacheKey = `sites-${companyId || 'all'}`
    
    
    // Check pending request (deduplication) to prevent duplicate simultaneous calls
    if (cacheKey in pendingRequests) {
      await pendingRequests[cacheKey]
      return
    }
    
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    
    const requestPromise = (async () => {
      try {
        const params: any = { $top: 20 }
        if (companyId) {
          params['$filter'] = `companyID eq '${companyId}'`
        }
        const res = await timesheetService.getSites(params)
        const items = (res || []).map((x: any) => ({ 
          id: x.projectID || x.ProjectId || x.projectId, 
          name: x.name || x.Name, 
          companyId: x.companyID || x.CompanyId || x.companyId, 
          projectId: x.projectID || x.ProjectId || x.projectId 
        })) as V2DropdownItem[]
        ctx.commit('SET_SITES', items) // Don't store cache key since we're not caching
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to load sites')
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()
    
    pendingRequests[cacheKey] = requestPromise
    await requestPromise
  },

  async searchSites(ctx: ActionContext<V2State, any>, payload: string | { query?: string; companyId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      const query = typeof payload === 'string' ? payload : payload?.query
      const companyId = typeof payload === 'object' ? payload?.companyId : undefined
      
      const filters: string[] = []
      if (query && query.trim().length > 0) {
        filters.push(`contains(name,'${query.replace(/'/g, "''")}')`)
      }
      if (companyId) {
        filters.push(`companyID eq '${companyId}'`)
      }
      if (filters.length) {
        params['$filter'] = filters.join(' and ')
      }
      
      const res = await timesheetService.getSites(params)
      const items = (res || []).map((x: any) => ({ 
        id: x.projectID || x.ProjectId || x.projectId, 
        name: x.name || x.Name, 
        companyId: x.companyID || x.CompanyId || x.companyId, 
        projectId: x.projectID || x.ProjectId || x.projectId 
      })) as V2DropdownItem[]
      ctx.commit('SET_SITES', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search sites')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async loadEquipments(ctx: ActionContext<V2State, any>, siteId?: string) {
    const cacheKey = `equipments-${siteId || 'all'}`
    
    // Check pending request (deduplication) to prevent duplicate simultaneous calls
    if (cacheKey in pendingRequests) {
      await pendingRequests[cacheKey]
      return
    }
    
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    
    const requestPromise = (async () => {
      try {
        const params: any = {}
        if (siteId) {
          params['$filter'] = `startswith(ProjectId,'${siteId}')`
        }
        params['$top'] = 50
        const res = await timesheetService.getEquipments(params)
        const items = (res?.value || []).map((x: V2Equipment) => ({ 
          id: x.EquipmentId, 
          name: x.Name,
          projectId: x.ProjectId,
          siteId: x.SiteId // Include SiteId for auto-mapping
        })) as V2DropdownItem[]
        ctx.commit('SET_EQUIPMENTS', items)
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to load equipments')
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()
    
    pendingRequests[cacheKey] = requestPromise
    await requestPromise
  },

  async searchEquipments(ctx: ActionContext<V2State, any>, { query, siteId }: { query: string, siteId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = {}
      let filter = ''
      if (query && query.trim().length > 0) {
        filter = `contains(Name,'${query.replace(/'/g, "''")}')`
      }
      if (siteId) {
        const siteFilter = `SiteId eq '${siteId}'`
        filter = filter ? `${filter} and ${siteFilter}` : siteFilter
      }
      if (filter) {
        params['$filter'] = filter
      }
      params['$top'] = 50
      const res = await timesheetService.getEquipments(params)
      const items = (res?.value || []).map((x: V2Equipment) => ({ 
        id: x.EquipmentId, 
        name: x.Name,
        projectId: x.ProjectId,
        siteId: x.SiteId // Include SiteId for auto-mapping
      })) as V2DropdownItem[]
      ctx.commit('SET_EQUIPMENTS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search equipments')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async loadWorkOrders(ctx: ActionContext<V2State, any>, projectId?: string) {
    const cacheKey = `workorders-${projectId || 'all'}`
    
    // Check pending request (deduplication) to prevent duplicate simultaneous calls
    if (cacheKey in pendingRequests) {
      await pendingRequests[cacheKey]
      return
    }
    
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    
    const requestPromise = (async () => {
      try {
        const params: any = {}
        if (projectId) {
          params['$filter'] = `startswith(ProjectId,'${projectId}')`
        }
        params['$top'] = 50
        const res = await timesheetService.getWorkOrders(params)
        const items = (res?.value || []).map((x: V2WorkOrder) => ({ 
          id: x.WorkOrderId, 
          name: x.Name,
          description: x.Description,
          projectId: x.ProjectId,
          equipmentId: x.EquipmentId // Include EquipmentId for auto-mapping
        })) as V2DropdownItem[]
        ctx.commit('SET_WORK_ORDERS', items)
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to load work orders')
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()
    
    pendingRequests[cacheKey] = requestPromise
    await requestPromise
  },

  async searchWorkOrders(ctx: ActionContext<V2State, any>, { query, projectId }: { query: string, projectId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = {}
      let filter = ''
      if (query && query.trim().length > 0) {
        filter = `contains(Name,'${query.replace(/'/g, "''")}')`
      }
      if (projectId) {
        const pidFilter = `startswith(ProjectId,'${projectId}')`
        filter = filter ? `${filter} and ${pidFilter}` : pidFilter
      }
      if (filter) {
        params['$filter'] = filter
      }
      params['$top'] = 50
      const res = await timesheetService.getWorkOrders(params)
      const items = (res?.value || []).map((x: V2WorkOrder) => ({ 
        id: x.WorkOrderId, 
        name: x.Name,
        description: x.Description,
        projectId: x.ProjectId,
        equipmentId: x.EquipmentId // Include EquipmentId for auto-mapping
      })) as V2DropdownItem[]
      ctx.commit('SET_WORK_ORDERS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search work orders')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },



  // Tasks (Rental: SiteId; Force: ProjectId + SiteId)
  async loadTasks(ctx: ActionContext<V2State, any>, { siteId, projectId }: { siteId?: string, projectId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = {}
      const filters: string[] = []
      if (siteId) filters.push(`SiteId eq '${siteId}'`)
      if (projectId) filters.push(`ProjectId eq '${projectId}'`)
      if (filters.length) params['$filter'] = filters.join(' and ')
      params['$top'] = 50
      const res = await timesheetService.getTasks(params)
      const items = (res?.value || []).map((x: V2Task) => ({ id: x.TaskId, name: x.Name, description: x.Description })) as V2DropdownItem[]
      ctx.commit('SET_TASKS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to load tasks')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },
  async searchTasks(ctx: ActionContext<V2State, any>, { query, siteId, projectId }: { query: string, siteId?: string, projectId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = {}
      const filters: string[] = []
      if (query && query.trim()) filters.push(`contains(Name,'${query.replace(/'/g, "''")}')`)
      if (siteId) filters.push(`SiteId eq '${siteId}'`)
      if (projectId) filters.push(`ProjectId eq '${projectId}'`)
      if (filters.length) params['$filter'] = filters.join(' and ')
      params['$top'] = 50
      const res = await timesheetService.getTasks(params)
      const items = (res?.value || []).map((x: V2Task) => ({ id: x.TaskId, name: x.Name, description: x.Description })) as V2DropdownItem[]
      ctx.commit('SET_TASKS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search tasks')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // Precise fetchers by ProjectId (merge into dropdown lists)
  async fetchSiteByProjectId(ctx: ActionContext<V2State, any>, projectId: string) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $filter: `ProjectID eq '${projectId}'`, $top: 1 }
      const res = await timesheetService.getSites(params)
      const list = Array.isArray(res) ? res : (res?.value || [])
      const items = list.map((x: any) => ({ 
        id: x.projectID || x.ProjectId, 
        name: x.name || x.Name, 
        companyId: x.companyID || x.CompanyId, 
        projectId: x.projectID || x.ProjectId 
      })) as V2DropdownItem[]
      if (items.length) {
        const current = (ctx.getters as any).dropdownSites as V2DropdownItem[]
        const merged = [items[0], ...current.filter(i => i.id !== items[0].id)]
        ctx.commit('SET_SITES', merged)
      }
      return items[0]
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to fetch site by ProjectId')
      return undefined
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async fetchEquipmentByProjectId(ctx: ActionContext<V2State, any>, projectId: string) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $filter: `ProjectID eq '${projectId}'`, $top: 1 }
      const res = await timesheetService.getAssets(params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId,
        name: x.name || x.Name,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyId,
        parentProjectId: x.parentProjectID || x.ParentProjectID,
      })) as V2DropdownItem[]
      if (items.length) {
        const current = (ctx.getters as any).dropdownEquipments as V2DropdownItem[]
        const merged = [items[0], ...current.filter(i => i.id !== items[0].id)]
        ctx.commit('SET_EQUIPMENTS', merged)
      }
      return items[0] // Return the first item
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to fetch equipment by ProjectId')
      return undefined
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async getWorkOrderDetails(ctx: ActionContext<V2State, any>, { projectId, companyId, workOrderId }: { projectId: string, companyId: string, workOrderId: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const res = await timesheetService.getWorkOrderById(projectId, companyId, workOrderId)
      return res
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to get work order details')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async getEquipmentDetails(ctx: ActionContext<V2State, any>, { projectId, companyId, equipmentId }: { projectId: string, companyId: string, equipmentId: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const res = await timesheetService.getEquipmentById(projectId, companyId, equipmentId)
      return res
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to get equipment details')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // === RENTAL API - load assets (fleet) for a site (optional) ===
  async loadAssets(ctx: ActionContext<V2State, any>, siteId?: string) {
    const cacheKey = `assets-${siteId || 'all'}`
    
    // Check pending request (deduplication) to prevent duplicate simultaneous calls
    if (cacheKey in pendingRequests) {
      await pendingRequests[cacheKey]
    }
    
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    
    const requestPromise = (async () => {
      try {
        const params: any = { $top: 20 }
        if (siteId) {
          params['$filter'] = `ParentProjectID eq '${siteId}'`;
        }
        const res = await timesheetService.getAssets(params)
        const items = (res || []).map((x: any) => ({
          id: x.projectID || x.ProjectId || x.projectId || x.id,
          name: x.name || x.Name,
          projectId: x.projectID || x.ProjectId || x.projectId || x.id,
          companyId: x.companyID || x.CompanyId || x.companyId,
          parentProjectId: x.parentProjectID || x.ParentProjectID || x.parentProjectId || x.siteId,
        })) as V2DropdownItem[]
        ctx.commit('SET_ASSETS', items)
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to load assets')
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()
    
    pendingRequests[cacheKey] = requestPromise
    await requestPromise
  },


  async searchAssets(ctx: ActionContext<V2State, any>, { query, siteId }: { query: string, siteId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      let filters = [];
      if (query && query.trim().length > 0) {
        filters.push(`contains(name,'${query.replace(/'/g, "''")}')`);
      }
      if (siteId) {
        filters.push(`ParentProjectID eq '${siteId}'`);
      }
      if (filters.length > 0) {
        params['$filter'] = filters.join(' and ');
      }
      const res = await timesheetService.getAssets(params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId || x.projectId || x.id,
        name: x.name || x.Name,
        projectId: x.projectID || x.ProjectId || x.projectId || x.id,
        companyId: x.companyID || x.CompanyId || x.companyId,
        parentProjectId: x.parentProjectID || x.ParentProjectID || x.parentProjectId || x.siteId,
      })) as V2DropdownItem[]
      ctx.commit('SET_EQUIPMENTS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search assets')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // === RENTAL API - load workorders for an asset (optional) ===
  async loadWorkOrdersByAsset(ctx: ActionContext<V2State, any>, assetId?: string) {
    const cacheKey = `workordersByAsset-${assetId || 'all'}`
    
    // Check pending request (deduplication) to prevent duplicate simultaneous calls
    if (cacheKey in pendingRequests) {
      await pendingRequests[cacheKey]
    }
    
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    
    const requestPromise = (async () => {
      try {
        const params: any = { $top: 20 }
        if (assetId) {
          params['$filter'] = `ParentProjectID eq '${assetId}'`;
        }
        const res = await timesheetService.getWorkOrdersByAsset(params)
        const items = (res || []).map((x: any) => ({
          id: x.projectID || x.ProjectId || x.projectId || x.id,
          name: x.name || x.Name,
          description: x.description || x.Description,
          projectId: x.projectID || x.ProjectId || x.projectId || x.id,
          companyId: x.companyID || x.CompanyId || x.companyId,
          parentProjectId: x.parentProjectID || x.ParentProjectID || x.parentProjectId || x.assetId,
        })) as V2DropdownItem[]
        ctx.commit('SET_WORK_ORDERS', items)
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to load work orders')
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()
    
    pendingRequests[cacheKey] = requestPromise
    await requestPromise
  },


  async searchWorkOrdersByAsset(ctx: ActionContext<V2State, any>, { query, assetId }: { query: string, assetId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      let filters = [];
      if (query && query.trim().length > 0) {
        filters.push(`contains(name,'${query.replace(/'/g, "''")}')`);
      }
      if (assetId) {
        filters.push(`ParentProjectID eq '${assetId}'`);
      }
      if (filters.length > 0) {
        params['$filter'] = filters.join(' and ');
      }
      const res = await timesheetService.getWorkOrdersByAsset(params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId || x.projectId || x.id,
        name: x.name || x.Name,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId || x.projectId || x.id,
        companyId: x.companyID || x.CompanyId || x.companyId,
        parentProjectId: x.parentProjectID || x.ParentProjectID || x.parentProjectId || x.assetId,
      })) as V2DropdownItem[]
      ctx.commit('SET_WORK_ORDERS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search work orders')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // === PROJECTS (Jobs) - Global or Site-specific ===
  async loadProjects(ctx: ActionContext<V2State, any>, { siteId }: { siteId?: string } = {}) {
    const cacheKey = `projects-${siteId || 'all'}`
    
    // Check pending request
    if (cacheKey in pendingRequests) {
      await pendingRequests[cacheKey]
      return
    }

    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)

    const requestPromise = (async () => {
      try {
        const params: any = { $top: 20 }
        const filters: string[] = []
        
        if (siteId) {
          filters.push(`ParentProjectID eq '${siteId}'`)
        }
        
        if (filters.length) {
            params['$filter'] = filters.join(' and ')
        }

        const res = await timesheetService.getProjects(params)

        const items = (res || []).map((x: any) => ({
          id: x.projectID || x.ProjectId || x.id,
          name: x.name || x.Name,
          description: x.description || x.Description,
          projectId: x.projectID || x.ProjectId || x.id,
          companyId: x.companyID || x.CompanyId || x.companyId,
          siteId: x.parentProjectID || x.ParentProjectID || x.siteId || x.parentProjectId,
        })) as V2DropdownItem[]
        ctx.commit('SET_PROJECTS', items)
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to load projects')
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()

    pendingRequests[cacheKey] = requestPromise
    await requestPromise
  },

  async searchProjects(ctx: ActionContext<V2State, any>, { query, siteId }: { query: string, siteId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      let res;
      const params: any = { $top: 20 }
      
      const filters: string[] = []
      if (query && query.trim().length > 0) {
        filters.push(`contains(Name,'${query.replace(/'/g, "''")}')`)
      }

      // Consistently use global projects endpoint with ParentProjectID filter
      if (siteId) {
        filters.push(`ParentProjectID eq '${siteId}'`)
      }
      
      if (filters.length) {
          params['$filter'] = filters.join(' and ')
      }
      
      res = await timesheetService.getProjects(params)

      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId || x.id,
        name: x.name || x.Name,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId || x.id,
        companyId: x.companyID || x.CompanyId || x.companyId,
        siteId: x.parentProjectID || x.ParentProjectID || x.siteId || x.parentProjectId,
      })) as V2DropdownItem[]
      ctx.commit('SET_PROJECTS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search projects')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // === RENTAL API - load projects (jobs) for a site ===
  async loadProjectsBySite(ctx: ActionContext<V2State, any>, siteId: string) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      const res = await timesheetService.getProjectsBySite(siteId, params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId || x.id,
        name: x.name || x.Name,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId || x.id,
        companyId: x.companyID || x.CompanyId || x.companyId,
        siteId: x.parentProjectID || x.ParentProjectID || x.siteId || x.parentProjectId,
      })) as V2DropdownItem[]
      ctx.commit('SET_PROJECTS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to load projects')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async searchProjectsBySite(ctx: ActionContext<V2State, any>, { query, siteId }: { query: string, siteId: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      if (query && query.trim().length > 0) {
        params['$filter'] = `contains(name,'${query.replace(/'/g, "''")}')`
      }
      const res = await timesheetService.getProjectsBySite(siteId, params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId || x.id,
        name: x.name || x.Name,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId || x.id,
        companyId: x.companyID || x.CompanyId || x.companyId,
        siteId: x.parentProjectID || x.ParentProjectID || x.siteId || x.parentProjectId,
      })) as V2DropdownItem[]
      ctx.commit('SET_PROJECTS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search projects')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // === OVERHEAD TASK CODES - for overhead mode ===
  async loadOverheadTaskCodes(ctx: ActionContext<V2State, any>, companyId?: string) {
    const cacheKey = `tasks-${companyId || 'all'}`
    
    // Check pending request (deduplication) to prevent duplicate simultaneous calls
    if (cacheKey in pendingRequests) {
      await pendingRequests[cacheKey]
      return
    }
    
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    
    const requestPromise = (async () => {
      try {
        const params: any = { $top: 20 }
        if (companyId) {
          params['$filter'] = `CompanyID eq '${companyId}'`
        }
        const res = await timesheetService.getOverheadTaskCodes(params)
        const items = (res || []).map((x: any) => ({
          id: x.overheadTaskCodeId || x.OverheadTaskCodeId || x.id,
          name: x.npName || x.NPName || x.name,
          code: x.npCode || x.NPCode || x.code,
          companyId: x.companyID || x.CompanyID || x.companyId,
        })) as V2DropdownItem[]
        ctx.commit('SET_TASKS', items)
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to load overhead task codes')
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()
    
    pendingRequests[cacheKey] = requestPromise
    await requestPromise
  },

  async searchOverheadTaskCodes(ctx: ActionContext<V2State, any>, { query, companyId }: { query?: string, companyId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      const filters: string[] = []
      if (query && query.trim().length > 0) {
        filters.push(`contains(NPName,'${query.replace(/'/g, "''")}')`)
      }
      if (companyId) {
        filters.push(`CompanyID eq '${companyId}'`)
      }
      if (filters.length) {
        params['$filter'] = filters.join(' and ')
      }
      const res = await timesheetService.getOverheadTaskCodes(params)
      const items = (res || []).map((x: any) => ({
        id: x.overheadTaskCodeId || x.OverheadTaskCodeId,
        name: x.npName || x.NPName,
        code: x.npCode || x.NPCode,
        companyId: x.companyID || x.CompanyID,
      })) as V2DropdownItem[]
      ctx.commit('SET_TASKS', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search overhead task codes')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // === ACTIVITIES - for Force non-overhead mode ===
  // === ACTIVITIES - for Force non-overhead mode ===
  async loadActivities(ctx: ActionContext<V2State, any>, projectId?: string) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      if (projectId) {
        params['$filter'] = `ProjectID eq '${projectId}'`
      }
      const res = await timesheetService.getActivities(params)
      const items = (res || []).map((x: any) => ({
        id: x.activityId || x.ActivityId || x.projectActivityId || x.ProjectActivityId,
        name: x.name || x.Name || x.activityName || x.ActivityName,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyID,
      })) as V2DropdownItem[]
      ctx.commit('SET_ACTIVITIES', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to load activities')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async searchActivities(ctx: ActionContext<V2State, any>, { query, projectId }: { query?: string, projectId?: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const params: any = { $top: 20 }
      const filters: string[] = []
      if (query && query.trim().length > 0) {
        const safeQuery = query.replace(/'/g, "''");
        filters.push(`(contains(ActivityName,'${safeQuery}') or contains(ActivityId,'${safeQuery}'))`)
      }
      if (projectId) {
        filters.push(`ProjectID eq '${projectId}'`)
      }
      if (filters.length) {
        params['$filter'] = filters.join(' and ')
      }
      const res = await timesheetService.getActivities(params)
      const items = (res || []).map((x: any) => ({
        id: x.activityId || x.ActivityId || x.projectActivityId || x.ProjectActivityId,
        name: x.name || x.Name || x.activityName || x.ActivityName,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyID,
      })) as V2DropdownItem[]
      ctx.commit('SET_ACTIVITIES', items)
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to search activities')
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async fetchJobById(ctx: ActionContext<V2State, any>, { siteId, jobId }: { siteId: string, jobId: string }) {
    try {
      // NOTE: Even though we have siteId, we can use global endpoint or site-specific.
      // But since we are moving to global endpoint /master/projects, let's use that.
      const params: any = { 
        $filter: `projectID eq '${jobId}'`,
        $top: 1 
      }
      const res = await timesheetService.getProjects(params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId,
        name: x.name || x.Name,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyId,
        siteId: x.parentProjectID || x.ParentProjectID,
      })) as V2DropdownItem[]
      
      if (items.length > 0) {
        // Inject into projects list if not already present
        const current = ctx.state.projects
        const exists = current.some(p => p.id === items[0].id)
        if (!exists) {
          ctx.commit('SET_PROJECTS', [items[0], ...current])
        }
        return items[0]
      }
      return null
    } catch (err: any) {
      return null
    }
  },

  async fetchProjectById(ctx: ActionContext<V2State, any>, projectId: string) {
    try {
      const params: any = { 
        $filter: `ProjectID eq '${projectId}'`,
        $top: 1 
      }
      const res = await timesheetService.getProjects(params)
      // Check if res is array or object with value
      const rawItems = Array.isArray(res) ? res : (res?.value || [])
      
      const items = rawItems.map((x: any) => ({
        id: x.projectID || x.ProjectId,
        name: x.name || x.Name,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyId,
        siteId: x.parentProjectID || x.ParentProjectID || x.SiteId,
      })) as V2DropdownItem[]
      
      if (items.length > 0) {
        // Inject into projects list if not already present
        const current = ctx.state.projects
        const exists = current.some(p => p.id === items[0].id)
        if (!exists) {
          ctx.commit('SET_PROJECTS', [items[0], ...current])
        }
        return items[0]
      }
      return null
    } catch (err: any) {
      return null
    }
  },

  async fetchActivityById(ctx: ActionContext<V2State, any>, { projectId, activityId }: { projectId: string, activityId: string }) {
    try {
      const params: any = { 
        $filter: `ActivityId eq '${activityId}'`, // Ensure casing matches backend model (ProjectActivityId/ActivityId)
        $top: 1 
      }
      const res = await timesheetService.getActivities(params)
      const items = (res || []).map((x: any) => ({
        id: x.activityId || x.ActivityId || x.projectActivityId || x.ProjectActivityId,
        name: x.name || x.Name || x.activityName || x.ActivityName,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyID,
      })) as V2DropdownItem[]
      
      if (items.length > 0) {
        // Inject into activities list if not already present
        const current = ctx.state.activities
        const exists = current.some(t => t.id === items[0].id)
        if (!exists) {
          ctx.commit('SET_ACTIVITIES', [items[0], ...current])
        }
        return items[0]
      }
      return null
    } catch (err: any) {
      return null
    }
  },

  async fetchSiteById(ctx: ActionContext<V2State, any>, siteId: string) {
    try {
      const params: any = { 
        $filter: `projectID eq '${siteId}'`,
        $top: 1 
      }
      const res = await timesheetService.getSites(params)
      const items = (res || []).map((x: V2Site) => ({ 
        id: x.projectID, 
        name: x.name, 
        companyId: x.companyID, 
        projectId: x.projectID 
      })) as V2DropdownItem[]
      
      if (items.length > 0) {
        // Inject into sites list if not already present
        const current = ctx.state.sites
        const exists = current.some(s => s.id === items[0].id)
        if (!exists) {
          ctx.commit('SET_SITES', [items[0], ...current])
        }
        return items[0]
      }
      return null
    } catch (err: any) {
      return null
    }
  },

  async fetchAssetById(ctx: ActionContext<V2State, any>, { siteId, assetId }: { siteId: string, assetId: string }) {
    try {
      const params: any = { 
        $filter: `projectID eq '${assetId}'`,
        $top: 1 
      }
      const res = await timesheetService.getAssets(params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId,
        name: x.name || x.Name,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyId,
        parentProjectId: x.parentProjectID || x.ParentProjectID,
      })) as V2DropdownItem[]
      
      if (items.length > 0) {
        // Inject into equipments list if not already present
        const current = ctx.state.equipments
        const exists = current.some(e => e.id === items[0].id)
        if (!exists) {
          ctx.commit('SET_EQUIPMENTS', [items[0], ...current])
        }
        return items[0]
      }
      return null
    } catch (err: any) {
      return null
    }
  },

  async fetchWorkOrderById(ctx: ActionContext<V2State, any>, { assetId, workOrderId }: { assetId: string, workOrderId: string }) {
    try {
      const params: any = { 
        $filter: `projectID eq '${workOrderId}'`,
        $top: 1 
      }
      const res = await timesheetService.getWorkOrdersByAsset(params)
      const items = (res || []).map((x: any) => ({
        id: x.projectID || x.ProjectId,
        name: x.name || x.Name,
        description: x.description || x.Description,
        projectId: x.projectID || x.ProjectId,
        companyId: x.companyID || x.CompanyId,
        parentProjectId: x.parentProjectID || x.ParentProjectID,
      })) as V2DropdownItem[]
      
      if (items.length > 0) {
        // Inject into workOrders list if not already present
        const current = ctx.state.workOrders
        const exists = current.some(w => w.id === items[0].id)
        if (!exists) {
          ctx.commit('SET_WORK_ORDERS', [items[0], ...current])
        }
        return items[0]
      }
      return null
    } catch (err: any) {
      return null
    }
  },

  // Create timesheet via V2 API
  async createTimesheet(ctx: ActionContext<V2State, any>, data: any) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const result = await timesheetService.createTimesheetV2(data)
      return result
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to create timesheet')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async updateTimesheet(ctx: ActionContext<V2State, any>, { id, data }: { id: number, data: any }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const response = await timesheetService.updateTimesheet(id, data)
      return response
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to update timesheet')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // Approve timesheet via V2 API
  async approveTimesheet(ctx: ActionContext<V2State, any>, data: { ids: number[] }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const result = await timesheetService.approveTimesheetV2(data)
      return result
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to approve timesheet')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // Get timesheet detail for edit mode
  async getTimesheetDetail(ctx: ActionContext<V2State, any>, id: number) {
    const cacheKey = `timesheet-detail-${id}`
    
    // Check pending request (deduplication)
    if (cacheKey in pendingRequests) {
      return pendingRequests[cacheKey]
    }
    
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    
    const requestPromise = (async () => {
      try {
        const result = await timesheetService.getTimesheetDetailV2(id)
        return result
      } catch (err: any) {
        ctx.commit('SET_ERROR', err?.message || 'Failed to get timesheet detail')
        throw err
      } finally {
        ctx.commit('SET_LOADING', false)
        delete pendingRequests[cacheKey]
      }
    })()
    
    pendingRequests[cacheKey] = requestPromise
    return requestPromise
  },

  async recallTimesheet(ctx: ActionContext<V2State, any>, id: number) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const result = await timesheetService.recallTimesheet(id)
      return result
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to recall timesheet')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // Cancel (Void) timesheet - sets status to 5
  async cancelTimesheet(ctx: ActionContext<V2State, any>, id: number) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const result = await timesheetService.cancelTimesheet(id)
      return result
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to cancel timesheet')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  // Decline timesheet with reason
  async declineTimesheet(ctx: ActionContext<V2State, any>, { id, reason }: { id: number, reason: string }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const result = await timesheetService.declineTimesheet(id, reason)
      return result
    } catch (err: any) {
      ctx.commit('SET_ERROR', err?.message || 'Failed to decline timesheet')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },


  async approveTimesheetWithChanges(ctx: ActionContext<V2State, any>, { id, payload }: { id: number, payload: any }) {
    ctx.commit('SET_LOADING', true)
    ctx.commit('SET_ERROR', null)
    try {
      const result = await timesheetService.approveWithChanges(id, payload)
      return result
    } catch (err: any) {
      // Failed to approve timesheet with changes
      ctx.commit('SET_ERROR', err?.data?.message || 'Failed to approve timesheet')
      throw err
    } finally {
      ctx.commit('SET_LOADING', false)
    }
  },

  async loadTimesheetsByDate(ctx: ActionContext<V2State, any>, { date, excludeId }: { date: string, excludeId?: number }) {
    try {
      // Get current user ID from store
      const currentUser = ctx.rootGetters['auth/currentUser']
      const userId = currentUser?.userId
      if (!userId) {
        return []
      }
      const result = await timesheetService.getTimesheetsByDate(date, userId, excludeId)
      return result?.value || result || []
    } catch (err: any) {
      return []
    }
  },

  // Upload signature image
  async uploadSignature(_ctx: ActionContext<V2State, any>, formData: FormData) {
    try {
      const result = await timesheetService.uploadSignature(formData)
      return result
    } catch (err: any) {
      throw err
    }
  },
}

const getters = {
  dropdownSites: (s: V2State) => s.sites,
  dropdownEquipments: (s: V2State) => s.equipments,
  dropdownAssets: (s: V2State) => s.equipments,
  dropdownWorkOrders: (s: V2State) => s.workOrders,
  dropdownProjects: (s: V2State) => s.projects,
  dropdownTasks: (s: V2State) => s.tasks,  // Overhead Task Codes only
  dropdownActivities: (s: V2State) => s.activities,  // Force Activities only
  loading: (s: V2State) => s.loading,
  error: (s: V2State) => s.error,
}

export const v2 = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
